import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { PUBLIC_HOST } from "../lib/domain";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINES = [
  { id: "muay_thai", label: "Muay Thaï" },
  { id: "kickboxing", label: "Kickboxing" },
  { id: "bjj", label: "Jiu-Jitsu Brésilien" },
  { id: "jjk", label: "Ju-Jitsu Japonais" },
  { id: "mma", label: "MMA" },
  { id: "petits_samourais", label: "Petits Samouraïs (3-6 ans)" },
  { id: "powerkick_her", label: "PowerKick Her (femmes)" },
];

export default function FranchiseLanding() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    city: "",
    country: "Canada",
    investment_capacity: "100k-250k",
    martial_arts_experience: "5-15ans",
    discipline_interest: [],
    timeline: "6-12_mois",
    has_location: false,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const toggleDiscipline = (id) => {
    setForm((f) => ({
      ...f,
      discipline_interest: f.discipline_interest.includes(id)
        ? f.discipline_interest.filter((d) => d !== id)
        : [...f.discipline_interest, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.city) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/franchise/lead`, form);
      setResult(data);
      toast.success("Candidature enregistrée. Kru Ralph vous contactera sous 5 jours ouvrés.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Thank you state =====
  if (result?.ok) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-16">
        <div className="max-w-2xl w-full">
          <div className="border border-amber-500/40 bg-zinc-950 rounded-2xl p-10 shadow-2xl">
            <div className="text-amber-400 text-xs tracking-[0.3em] mb-4">KAIZEN SYSTEM · FRANCHISE</div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="franchise-thankyou-title">
              Candidature reçue.
            </h1>
            <p className="text-zinc-300 leading-relaxed mb-6">
              Merci d'avoir initié le processus de qualification.
              <br />
              <span className="text-amber-300">Référence : <strong>{result.reference}</strong></span>
            </p>
            <p className="text-zinc-400 text-sm mb-8">
              {result.message}
            </p>
            {result.pdf_url && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${result.pdf_url}`}
                target="_blank"
                rel="noreferrer"
                data-testid="franchise-download-pdf-link"
              >
                <Button
                  className="bg-amber-500 text-black hover:bg-amber-400 font-semibold tracking-wide"
                  data-testid="franchise-download-pdf-button"
                >
                  📄 Télécharger le dossier de pré-qualification
                </Button>
              </a>
            )}
            <div className="mt-10 pt-8 border-t border-zinc-800 text-xs text-zinc-500">
              KAIZEN SYSTEM™ · Méthode Kaizen — amélioration continue<br />
              {PUBLIC_HOST} · Val-d'Or, Québec, Canada
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Landing + Form =====
  return (
    <div className="min-h-screen bg-black text-white" data-testid="franchise-landing">
      {/* HERO */}
      <section className="relative px-6 sm:px-12 pt-16 pb-24 border-b border-zinc-900 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #d4af37, transparent 40%), radial-gradient(circle at 80% 70%, #c0392b, transparent 40%)" }} />
        <div className="max-w-6xl mx-auto relative">
          <div className="flex items-center gap-3 mb-8">
            <img
              src="/logos/kaizen_system/generated/kaizen_system_master_color_transparent.png"
              alt="Kaizen System"
              className="h-12 w-12 object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div>
              <div className="text-amber-400 text-[10px] tracking-[0.4em]">KAIZEN SYSTEM™</div>
              <div className="text-zinc-500 text-[10px] tracking-[0.3em]">FRANCHISE GLOBALE</div>
            </div>
          </div>

          <h1 className="text-5xl sm:text-7xl font-bold leading-[0.95] mb-8 tracking-tight" data-testid="franchise-hero-title">
            Devenez le<br />
            <span className="text-amber-400">franchisé Kaizen</span><br />
            <span className="text-zinc-500">de votre région.</span>
          </h1>

          <p className="text-xl text-zinc-300 max-w-2xl leading-relaxed mb-10">
            Un système d'arts martiaux <strong className="text-white">premium, durable, et éprouvé</strong> —
            propulsé par la technologie, supporté par Kru Ralph D. Jean-Pierre.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a href="#postuler">
              <Button
                size="lg"
                className="bg-amber-500 text-black hover:bg-amber-400 font-semibold tracking-wide px-8"
                data-testid="franchise-hero-cta-postuler"
              >
                Postuler maintenant →
              </Button>
            </a>
            <a href="#opportunite">
              <Button
                size="lg"
                variant="outline"
                className="border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 px-8"
                data-testid="franchise-hero-cta-learn"
              >
                Découvrir l'opportunité
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* VISION */}
      <section id="opportunite" className="px-6 sm:px-12 py-20 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">1. LA VISION</div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-8 tracking-tight">
            Le Lululemon des arts martiaux<br />
            <span className="text-zinc-500">— qualité Mercedes.</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-zinc-300">
            <div data-testid="franchise-pillar-systeme">
              <div className="text-amber-400 text-3xl font-bold mb-2">01</div>
              <div className="font-semibold text-white mb-2">Système central</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Tu hérites du noyau Kaizen : curriculum, carnets, certificats, billetterie, comptabilité, IA Wingman, CRM —
                tout est déjà construit et maintenu pour toi.
              </p>
            </div>
            <div data-testid="franchise-pillar-premium">
              <div className="text-amber-400 text-3xl font-bold mb-2">02</div>
              <div className="font-semibold text-white mb-2">Identité premium</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Charte visuelle noir + or + rouge, logos haute résolution, contrats notariés, certificats officiels signés.
                Aucun raccourci.
              </p>
            </div>
            <div data-testid="franchise-pillar-evolution">
              <div className="text-amber-400 text-3xl font-bold mb-2">03</div>
              <div className="font-semibold text-white mb-2">Méthode Kaizen</div>
              <p className="text-sm text-zinc-400 leading-relaxed">
                Amélioration continue. Une seule mise à jour du système central
                bénéficie à TOUS les franchisés simultanément. Tu ne restes jamais en arrière.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CHIFFRES */}
      <section className="px-6 sm:px-12 py-20 border-b border-zinc-900 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">2. CE QUE TU REÇOIS</div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-12 tracking-tight">Le pack franchise.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: "Modules logiciels", value: "+80", note: "CRM, billetterie, contrats, certificats…" },
              { label: "Disciplines incluses", value: "7", note: "Muay Thaï · BJJ · MMA · JJK · Kids · PowerKick Her" },
              { label: "Logos & assets", value: "11+", note: "Master, disciplines, t-shirts, bannières" },
              { label: "Formation Kru Ralph", value: "1:1", note: "Visio bi-mensuelle pendant 6 mois" },
            ].map((c, i) => (
              <div key={i} className="border border-zinc-800 rounded-xl p-6 bg-black" data-testid={`franchise-stat-${i}`}>
                <div className="text-5xl font-bold text-amber-400 mb-2">{c.value}</div>
                <div className="text-white font-semibold mb-1">{c.label}</div>
                <div className="text-xs text-zinc-500">{c.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESSUS */}
      <section className="px-6 sm:px-12 py-20 border-b border-zinc-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">3. LE PROCESSUS</div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-12 tracking-tight">5 étapes simples.</h2>
          <ol className="space-y-6 text-zinc-300">
            {[
              "Candidature en ligne (5 min) — formulaire ci-dessous.",
              "Pré-qualification PDF générée automatiquement — Kru Ralph la reçoit instantanément.",
              "Appel découverte (45 min visio) — capacité, vision, localisation.",
              "Contrat franchise signé chez notaire — Kaizen System Inc.",
              "Onboarding 30 jours — tu démarres avec ton système prêt à vendre.",
            ].map((step, i) => (
              <li key={i} className="flex gap-6" data-testid={`franchise-step-${i + 1}`}>
                <div className="text-amber-400 font-bold text-2xl shrink-0 w-12">0{i + 1}.</div>
                <div className="pt-1">{step}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PROGRESSION 0 → 60 MOIS */}
      <ProgressionSection />

      {/* ROI CALCULATOR */}
      <RoiSection
        capacity={form.investment_capacity}
        disciplines={form.discipline_interest}
        onCapacityChange={(v) => setForm({ ...form, investment_capacity: v })}
      />

      {/* FORM */}
      <section id="postuler" className="px-6 sm:px-12 py-20 bg-zinc-950">
        <div className="max-w-3xl mx-auto">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">5. POSTULER</div>
          <h2 className="text-4xl sm:text-5xl font-bold mb-2 tracking-tight">Ta candidature.</h2>
          <p className="text-zinc-400 mb-10">
            5 minutes. Pas de pression. Kru Ralph te contacte personnellement sous 5 jours ouvrés.
          </p>

          <form onSubmit={submit} className="space-y-6" data-testid="franchise-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nom complet *" testid="franchise-input-name">
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Prénom Nom"
                  data-testid="franchise-input-name-field"
                  required
                />
              </Field>
              <Field label="Courriel *" testid="franchise-input-email">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="vous@exemple.com"
                  data-testid="franchise-input-email-field"
                  required
                />
              </Field>
              <Field label="Téléphone *" testid="franchise-input-phone">
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 819 123 4567"
                  data-testid="franchise-input-phone-field"
                  required
                />
              </Field>
              <Field label="Ville *" testid="franchise-input-city">
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Montréal"
                  data-testid="franchise-input-city-field"
                  required
                />
              </Field>
              <Field label="Pays" testid="franchise-input-country">
                <Input
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  data-testid="franchise-input-country-field"
                />
              </Field>
              <Field label="Horizon d'ouverture" testid="franchise-input-timeline">
                <Select value={form.timeline} onValueChange={(v) => setForm({ ...form, timeline: v })}>
                  <SelectTrigger data-testid="franchise-select-timeline"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediat">Immédiat (0-3 mois)</SelectItem>
                    <SelectItem value="3-6_mois">3 à 6 mois</SelectItem>
                    <SelectItem value="6-12_mois">6 à 12 mois</SelectItem>
                    <SelectItem value="12mois+">Plus de 12 mois</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Capacité d'investissement (CAD)" testid="franchise-input-capacity">
              <Select value={form.investment_capacity} onValueChange={(v) => setForm({ ...form, investment_capacity: v })}>
                <SelectTrigger data-testid="franchise-select-capacity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="<100k">Moins de 100 000 $</SelectItem>
                  <SelectItem value="100k-250k">100 000 $ — 250 000 $</SelectItem>
                  <SelectItem value="250k-500k">250 000 $ — 500 000 $</SelectItem>
                  <SelectItem value="500k+">Plus de 500 000 $</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <Field label="Expérience en arts martiaux" testid="franchise-input-experience">
              <Select value={form.martial_arts_experience} onValueChange={(v) => setForm({ ...form, martial_arts_experience: v })}>
                <SelectTrigger data-testid="franchise-select-experience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aucune">Aucune (investisseur)</SelectItem>
                  <SelectItem value="<5ans">Moins de 5 ans</SelectItem>
                  <SelectItem value="5-15ans">5 à 15 ans</SelectItem>
                  <SelectItem value="15ans+">15 ans et plus</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <div>
              <Label className="text-zinc-300 text-sm">Disciplines visées (plusieurs choix)</Label>
              <div className="grid sm:grid-cols-2 gap-2 mt-3">
                {DISCIPLINES.map((d) => (
                  <label
                    key={d.id}
                    className="flex items-center gap-3 p-3 border border-zinc-800 rounded-lg bg-black hover:border-amber-500/50 cursor-pointer transition-colors"
                    data-testid={`franchise-discipline-${d.id}`}
                  >
                    <Checkbox
                      checked={form.discipline_interest.includes(d.id)}
                      onCheckedChange={() => toggleDiscipline(d.id)}
                      data-testid={`franchise-discipline-${d.id}-checkbox`}
                    />
                    <span className="text-sm text-zinc-300">{d.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-3 p-4 border border-zinc-800 rounded-lg bg-black">
              <Checkbox
                checked={form.has_location}
                onCheckedChange={(v) => setForm({ ...form, has_location: !!v })}
                data-testid="franchise-checkbox-location"
              />
              <span className="text-sm text-zinc-300">J'ai déjà un local identifié ou une option de bail.</span>
            </label>

            <Field label="Message (optionnel)" testid="franchise-input-message">
              <Textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Parle-moi de ta vision, ton équipe, ta région…"
                data-testid="franchise-input-message-field"
              />
            </Field>

            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="w-full bg-amber-500 text-black hover:bg-amber-400 font-semibold tracking-wide text-lg py-6"
              data-testid="franchise-submit-button"
            >
              {submitting ? "Envoi en cours…" : "Soumettre ma candidature →"}
            </Button>
            <p className="text-xs text-zinc-500 text-center">
              En soumettant, vous acceptez que vos données soient traitées par Kaizen System Inc. à des fins de qualification franchise uniquement.
            </p>
          </form>
        </div>
      </section>

      <footer className="px-6 sm:px-12 py-10 text-xs text-zinc-600 text-center">
        KAIZEN SYSTEM™ — Franchise globale d'arts martiaux premium · {PUBLIC_HOST}<br />
        Val-d'Or, Québec, Canada · Méthode Kaizen — amélioration continue
      </footer>
    </div>
  );
}

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <Label className="text-zinc-300 text-sm mb-2 block">{label}</Label>
    {children}
  </div>
);

// ============================================================================
// PROGRESSION — Feuille de route franchisé 0 → 60 mois
// ============================================================================

const PROGRESSION = [
  {
    period: "0 – 3 mois",
    phase: "Installation",
    objectives: "Installer la structure de base : local, horaires, branding, systèmes. Mettre en place les premiers cours et processus d'accueil.",
    results: "École prête à opérer, premiers élèves inscrits, routines de service client en place, équipe de base formée.",
  },
  {
    period: "3 – 6 mois",
    phase: "Stabilisation",
    objectives: "Stabiliser les groupes d'âge et les horaires. Activer le système de progression (ceintures, niveaux, carnets).",
    results: "Groupes réguliers par âge/niveau, élèves qui reviennent chaque semaine, premières évaluations et promotions, feedback structuré.",
  },
  {
    period: "6 – 12 mois",
    phase: "Consolidation",
    objectives: "Consolider la base d'élèves. Introduire les premiers événements internes (examens, démonstrations, mini-compétitions).",
    results: "Base d'élèves stable, communauté locale installée, culture de progression, premiers événements réussis, rétention suivie.",
  },
  {
    period: "12 – 24 mois",
    phase: "Circuit régional",
    objectives: "Entrer dans le circuit régional (tournois, stages, camps). Développer athlètes et coachs plus avancés. Optimiser l'organisation.",
    results: "Présence visible dans la région, athlètes identifiés, coachs plus autonomes, processus rodés, rentabilité en progression.",
  },
  {
    period: "24 – 36 mois",
    phase: "Pôle régional",
    objectives: "Renforcer le rôle de pôle régional. Accéder à des événements provinciaux / nationaux. Structurer un noyau élite.",
    results: "École reconnue comme référence dans sa zone, participation à des événements majeurs, noyau d'athlètes et coachs élite identifié.",
  },
  {
    period: "36 – 60 mois",
    phase: "Maturité & expansion",
    objectives: "Atteindre la maturité du modèle local et ouvrir la porte à une expansion (2ᵉ point, centre plus grand, rôle national).",
    results: "École stable, rentable, respectée, capable de former d'autres coachs, support au réseau, potentiel d'expansion clairement identifié.",
  },
];

const ProgressionSection = () => (
  <section id="progression" className="px-6 sm:px-12 py-20 border-b border-zinc-900 bg-zinc-950" data-testid="franchise-progression-section">
    <div className="max-w-6xl mx-auto">
      <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">VOTRE FEUILLE DE ROUTE</div>
      <h2 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">
        Du jour 1 à la maturité.
      </h2>
      <p className="text-zinc-400 max-w-2xl mb-12 leading-relaxed">
        Une progression réaliste, étape par étape. À chaque horizon de temps, vous savez sur quoi
        travailler — et ce que vous pouvez espérer comme résultat.
      </p>

      {/* Grille démonstrative */}
      <div className="space-y-4">
        {PROGRESSION.map((row, i) => (
          <div
            key={i}
            className="grid md:grid-cols-[160px_1fr_1fr] gap-5 border border-zinc-800 rounded-2xl p-6 bg-black hover:border-amber-500/40 transition-colors"
            data-testid={`franchise-progression-${i}`}
          >
            <div>
              <div className="text-2xl font-bold text-amber-400">{row.period}</div>
              <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mt-1">{row.phase}</div>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.25em] text-zinc-600 mb-1">OBJECTIFS</div>
              <p className="text-sm text-zinc-300 leading-relaxed">{row.objectives}</p>
            </div>
            <div>
              <div className="text-[11px] tracking-[0.25em] text-zinc-600 mb-1">RÉSULTATS ATTENDUS</div>
              <p className="text-sm text-zinc-400 leading-relaxed">{row.results}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Encadré résumé */}
      <div className="mt-10 border border-amber-500/30 rounded-2xl p-7 bg-gradient-to-br from-amber-500/5 to-transparent" data-testid="franchise-progression-summary">
        <div className="text-[11px] tracking-[0.3em] text-amber-400 mb-3">EN RÉSUMÉ</div>
        <p className="text-zinc-300 leading-relaxed">
          En <strong className="text-white">3 mois</strong>, vous installez la base. En <strong className="text-white">6 à 12 mois</strong>,
          vous stabilisez vos groupes et activez le système de progression. En <strong className="text-white">12 à 24 mois</strong>,
          vous entrez dans le circuit régional et consolidez la rentabilité. En <strong className="text-white">24 à 36 mois</strong>,
          vous devenez une référence régionale avec un noyau élite. En <strong className="text-white">3 à 5 ans</strong>,
          vous atteignez une maturité complète : école stable, rentable, respectée, prête à se développer encore.
        </p>
      </div>

      <p className="text-xs text-zinc-600 mt-6 max-w-3xl leading-relaxed">
        Cette feuille de route est indicative. Le rythme réel dépend du marché local, de l'exécution et du respect
        du système Kaizen — nous l'ajustons ensemble lors de l'appel découverte.
      </p>
    </div>
  </section>
);

// ============================================================================
// ROI CALCULATOR — Kaizen V1
// ============================================================================
// Hypothèses conservatrices basées sur le réseau Impact Élite Martial Val-d'Or :
//   - Frais membre moyen : 165 $/mois (mix abos + drop-in + cours privés + merch)
//   - +25 $/mois si membre prend ≥2 disciplines
//   - Charges opérationnelles : 60% du CA (loyer, staff, énergie, assurances)
//   - Royautés Kaizen : 6% du CA
//   - EBITDA net cible : ~34%
//   - Saisonnalité : 11 mois facturés / an (juillet creux)

const CAPACITY_SCALE = {
  "<100k":     { invest: 75000,  members_low: 50,  members_high: 90,  label: "Studio compact" },
  "100k-250k": { invest: 175000, members_low: 90,  members_high: 160, label: "Dojo standard" },
  "250k-500k": { invest: 350000, members_low: 160, members_high: 260, label: "Dojo premium" },
  "500k+":     { invest: 600000, members_low: 260, members_high: 420, label: "Flagship multi-disciplines" },
};

const fmtCAD = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));

const RoiSection = ({ capacity, disciplines, onCapacityChange }) => {
  const scale = CAPACITY_SCALE[capacity] || CAPACITY_SCALE["100k-250k"];
  const disciplineCount = Math.max(1, disciplines?.length || 1);
  const avgFee = 165 + (disciplineCount >= 2 ? 25 : 0) + (disciplineCount >= 4 ? 15 : 0);

  const monthlyRevLow = scale.members_low * avgFee;
  const monthlyRevHigh = scale.members_high * avgFee;
  const annualRevLow = monthlyRevLow * 11;
  const annualRevHigh = monthlyRevHigh * 11;
  const ebitdaLow = annualRevLow * 0.34;
  const ebitdaHigh = annualRevHigh * 0.34;
  const paybackLow = scale.invest / Math.max(1, ebitdaHigh);
  const paybackHigh = scale.invest / Math.max(1, ebitdaLow);

  return (
    <section id="roi" className="px-6 sm:px-12 py-20 border-b border-zinc-900 bg-black" data-testid="franchise-roi-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-amber-400 text-xs tracking-[0.4em] mb-4">4. PROJECTION FINANCIÈRE</div>
        <h2 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">
          Ton retour potentiel.
        </h2>
        <p className="text-zinc-400 max-w-2xl mb-10">
          Estimation conservatrice basée sur les performances du réseau Kaizen.
          Ajuste ta capacité d'investissement pour voir le scénario qui te correspond.
        </p>

        {/* Capacity selector pills */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="franchise-roi-capacity-selector">
          {Object.entries(CAPACITY_SCALE).map(([key, val]) => (
            <button
              key={key}
              type="button"
              onClick={() => onCapacityChange(key)}
              data-testid={`franchise-roi-cap-${key}`}
              className={
                "px-5 py-3 rounded-full border text-sm font-medium tracking-wide transition-all " +
                (capacity === key
                  ? "bg-amber-500 text-black border-amber-500 shadow-[0_0_20px_-5px_rgba(212,175,55,0.5)]"
                  : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-amber-500/40 hover:bg-zinc-900")
              }
            >
              <div className="text-xs opacity-70">{val.label}</div>
              <div>{key === "<100k" ? "<100 k$" : key === "500k+" ? "500 k$ +" : key.replace("-", " — ").replace("k", " k$")}</div>
            </button>
          ))}
        </div>

        {/* Metrics grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <RoiCard
            label="Membres à 12 mois"
            value={`${scale.members_low} – ${scale.members_high}`}
            note="conservateur → optimiste"
            testid="franchise-roi-members"
          />
          <RoiCard
            label="Revenu mensuel"
            value={`${fmtCAD(monthlyRevLow)} – ${fmtCAD(monthlyRevHigh)}`}
            note={`≈ ${avgFee} $/membre/mois`}
            testid="franchise-roi-monthly"
          />
          <RoiCard
            label="Revenu annuel"
            value={`${fmtCAD(annualRevLow)} – ${fmtCAD(annualRevHigh)}`}
            note="11 mois facturés"
            testid="franchise-roi-annual"
          />
          <RoiCard
            label="EBITDA estimé"
            value={`${fmtCAD(ebitdaLow)} – ${fmtCAD(ebitdaHigh)}`}
            note="marge nette ≈ 34%"
            highlight
            testid="franchise-roi-ebitda"
          />
        </div>

        {/* Payback callout */}
        <div className="grid md:grid-cols-2 gap-5">
          <div className="border border-amber-500/30 rounded-2xl p-6 bg-gradient-to-br from-amber-500/5 to-transparent" data-testid="franchise-roi-payback">
            <div className="text-xs text-amber-400 tracking-[0.3em] mb-2">RETOUR SUR INVESTISSEMENT</div>
            <div className="text-3xl font-bold text-white mb-1">
              {paybackLow.toFixed(1)} – {paybackHigh.toFixed(1)} ans
            </div>
            <div className="text-sm text-zinc-400">
              Pour récupérer ton investissement initial de <strong className="text-amber-300">{fmtCAD(scale.invest)}</strong>.
              Après ce point, l'EBITDA devient profit pur année après année.
            </div>
          </div>
          <div className="border border-zinc-800 rounded-2xl p-6 bg-zinc-950" data-testid="franchise-roi-assumptions">
            <div className="text-xs text-zinc-500 tracking-[0.3em] mb-2">HYPOTHÈSES DU MODÈLE</div>
            <ul className="text-sm text-zinc-400 space-y-1.5 leading-relaxed">
              <li>• Frais membre moyen : <strong className="text-zinc-200">{avgFee} $/mois</strong> ({disciplineCount} discipline{disciplineCount > 1 ? "s" : ""} sélectionnée{disciplineCount > 1 ? "s" : ""})</li>
              <li>• Charges opérationnelles : 60% du CA</li>
              <li>• Royautés Kaizen System : 6% du CA</li>
              <li>• Saisonnalité : 11 mois facturés / an</li>
              <li>• Hors revenus boutique, événements, commandites</li>
            </ul>
          </div>
        </div>

        <p className="text-xs text-zinc-600 mt-8 max-w-3xl leading-relaxed">
          ⚠️ Cette projection est indicative et ne constitue pas une garantie de résultat. Les performances réelles
          dépendent de l'exécution, du marché local et du respect du système Kaizen. Les chiffres précis seront
          analysés ensemble lors de l'appel découverte.
        </p>
      </div>
    </section>
  );
};

const RoiCard = ({ label, value, note, highlight = false, testid }) => (
  <div
    className={
      "rounded-2xl p-5 border " +
      (highlight
        ? "bg-amber-500/10 border-amber-500/40"
        : "bg-zinc-950 border-zinc-800")
    }
    data-testid={testid}
  >
    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{label}</div>
    <div className={"text-2xl font-bold mb-1 " + (highlight ? "text-amber-300" : "text-white")}>
      {value}
    </div>
    <div className="text-xs text-zinc-500">{note}</div>
  </div>
);
